/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';
import { ExportActionIds } from '@veeam-vspc/shared/components';
import { ConfigStates } from '@veeam-vspc/shared/core';

import type { UserConfigData } from '@veeam-vspc/shared/core';

import { ComputersActionIds, ConfigActionIds, ConfigAdvancedFilterItemsIds, ConfigItemIds, ConfigSectionIds } from 'core/enums';
import { RouteIds, RoutePaths } from '../../enums';
import { allConfigsRoutes, allCustomRoutes, allHomeRoutes } from '../routes';
import { AppPortalLayout } from 'views/components/layouts/AppPortalLayout';
import { AppPortalBodyLayout } from 'views/components/layouts/AppPortalBodyLayout';

export const vacReadonlyOperatorConfig: UserConfigData<typeof UserRoleRepresentation.VacReadonlyOperator, ConfigSectionIds> = {
    role: UserRoleRepresentation.VacReadonlyOperator,
    routes: [
        allCustomRoutes[RouteIds.Login],
        {
            path: '',
            component: AppPortalLayout,
            items: [
                {
                    path: RoutePaths.Home,
                    component: AppPortalBodyLayout,
                    items: [
                        allHomeRoutes[RouteIds.Overview],
                        allHomeRoutes[RouteIds.JobsSummary],
                        allHomeRoutes[RouteIds.ActiveAlarms],
                        allHomeRoutes[RouteIds.ProtectedDataSummary],
                        allHomeRoutes[RouteIds.Tops],
                        allHomeRoutes[RouteIds.BackupJobs],
                        allHomeRoutes[RouteIds.FailoverPlans],
                        allHomeRoutes[RouteIds.ProtectedData],
                        allHomeRoutes[RouteIds.Companies],
                        allHomeRoutes[RouteIds.Discovery],
                        allHomeRoutes[RouteIds.BillingSummary],
                        allHomeRoutes[RouteIds.Invoices],
                        allHomeRoutes[RouteIds.Reports],
                    ],
                },
                {
                    path: RoutePaths.Configs,
                    component: AppPortalBodyLayout,
                    items: [
                        allConfigsRoutes[RouteIds.RolesUsers],
                    ],
                },
            ],
        },
        allCustomRoutes[RouteIds.Api],
        {
            path: '*',
            redirectTo: RoutePaths.NotFound,
        },
    ],
    sections: [
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByConsole,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByBackupServer,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesVirtualInfrastructure,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsFileSharesLocal,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsDataBackupObjectStorage,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataComputers,
            actions: {
                [ComputersActionIds.FileLevelRestorePortalTBD]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsCloudDatabases,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsFileSharesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataCloudDatabases,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataFileSharesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarms,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ReportsConfiguration,
            defaultActionsState: ConfigStates.Hidden,
        },
        {
            id: ConfigSectionIds.ReportsAllReports,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.ActionToolbarViewReports]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ReportsFilterToolbarAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.ReportsManagingCompany]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.ReportsReportConfiguration]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.InvoicesConfiguration,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.FilterToolbarCompanySearch]: ConfigStates.Visible,
                [ConfigActionIds.FilterToolbarScheduleType]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.InvoicesAllInvoices,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.FilterToolbarCompanySearch]: ConfigStates.Visible,
                [ConfigActionIds.FilterToolbarStatus]: ConfigStates.Visible,
                [ConfigActionIds.FilterToolbarDatePeriod]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.EditUserProfileWizard,
            items: {
                loginInfoStep: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarmsFilterToolbarAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.AlarmProductVspc]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.AlarmProductCloudConnect]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.AlarmScope]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.DiscoveryComputersRules,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.ActionToolbarViewDiscoveredComputers]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.DiscoveryComputersComputers,
            actions: {
                [ConfigActionIds.ActionToolbarDeployAgentSingle]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarDeployAgentGroup]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarAutoDeploy]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarChangeSettings]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarSetCompany]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarManagementAgent]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarInstallVbr]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarDownloadAgentGroup]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarDownloadAgentSingle]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarInstallVbrSeparator]: ConfigStates.Hidden,
                [ConfigActionIds.ActionToolbarChangeSettingSeparator]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.FailoverPlansFailoverPlans,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.DiscoveryTaskDetailsDialog,
            actions: {
                [ConfigActionIds.ClearLogs]: ConfigStates.Hidden,
                [ConfigActionIds.CancelDeployment]: ConfigStates.Hidden,
                [ConfigActionIds.DownloadLogs]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BillingSummaryCloudConnectResourcesCloudConnectBackup,
            items: {
                [ConfigItemIds.StorageUsageWidget]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesVirtualInfrastructureAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.BackupJobsVMsVirtualInfrastructureBackupServerType]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.BackupJobsVMsVirtualInfrastructurePlatformType]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.CompaniesLocations,
            actions: {
                [ConfigActionIds.New]: ConfigStates.Hidden,
                [ConfigActionIds.Edit]: ConfigStates.Hidden,
                [ConfigActionIds.Remove]: ConfigStates.Hidden,
            },
        },
    ],
};
